/**
 * Minimal markdown tokenizer that recognizes fenced code blocks, inline code
 * backticks, and plain text. No regex — explicit state machine walking the
 * string once.
 */

export type MarkdownToken =
  | { kind: 'text'; value: string }
  | { kind: 'code-block'; language: string; value: string }
  | { kind: 'inline-code'; value: string }
  | { kind: 'link'; text: string; url: string };

const FENCE = '```';
const FENCE_LEN = 3;

/**
 * Tokenize a markdown string. Rules:
 *   - ```lang\n...\n```  → code-block token
 *   - `code`             → inline-code token
 *   - anything else      → text token
 *
 * Unterminated fences / backticks are treated as plain text.
 */
export function tokenizeMarkdown(input: string): MarkdownToken[] {
  const out: MarkdownToken[] = [];
  let cursor = 0;
  const pushText = (end: number): void => {
    if (end > cursor) out.push({ kind: 'text', value: input.substring(cursor, end) });
  };

  while (cursor < input.length) {
    if (startsWith(input, cursor, FENCE)) {
      const block = readCodeBlock(input, cursor);
      if (block) {
        pushText(cursor);
        out.push(block.token);
        cursor = block.nextCursor;
        continue;
      }
    }
    if (input[cursor] === '`') {
      const inline = readInlineCode(input, cursor);
      if (inline) {
        pushText(cursor);
        out.push(inline.token);
        cursor = inline.nextCursor;
        continue;
      }
    }
    if (input[cursor] === '[') {
      const link = readLink(input, cursor);
      if (link) {
        pushText(cursor);
        out.push(link.token);
        cursor = link.nextCursor;
        continue;
      }
    }
    cursor += 1;
  }
  pushText(input.length);
  return out;
}

// ── Internal walkers ─────────────────────────────────────────────────────

/** Match a fenced code block starting at `start`. */
function readCodeBlock(
  input: string,
  start: number,
): { token: MarkdownToken; nextCursor: number } | null {
  const afterOpenFence = start + FENCE_LEN;
  const langEnd = input.indexOf('\n', afterOpenFence);
  if (langEnd === -1) return null;
  const language = input.substring(afterOpenFence, langEnd).trim();
  const bodyStart = langEnd + 1;
  const closeFence = input.indexOf(FENCE, bodyStart);
  if (closeFence === -1) return null;
  const body = stripTrailingNewline(input.substring(bodyStart, closeFence));
  return {
    token: { kind: 'code-block', language, value: body },
    nextCursor: closeFence + FENCE_LEN,
  };
}

/** Match an inline `code` span starting at `start`. */
function readInlineCode(
  input: string,
  start: number,
): { token: MarkdownToken; nextCursor: number } | null {
  const closing = input.indexOf('`', start + 1);
  if (closing === -1) return null;
  const body = input.substring(start + 1, closing);
  if (body.includes('\n')) return null;
  return {
    token: { kind: 'inline-code', value: body },
    nextCursor: closing + 1,
  };
}

/**
 * Match a `[text](url)` link starting at `start`. Uses explicit index
 * scanning (no regex). Returns null if the syntax isn't complete.
 */
function readLink(input: string, start: number): { token: MarkdownToken; nextCursor: number } | null {
  const textEnd = input.indexOf(']', start + 1);
  if (textEnd === -1) return null;
  if (input[textEnd + 1] !== '(') return null;
  const urlEnd = input.indexOf(')', textEnd + 2);
  if (urlEnd === -1) return null;
  const text = input.substring(start + 1, textEnd);
  const url = input.substring(textEnd + 2, urlEnd);
  if (url.length === 0) return null;
  return {
    token: { kind: 'link', text, url },
    nextCursor: urlEnd + 1,
  };
}

/** Check whether `input` at `position` starts with `needle`. */
function startsWith(input: string, position: number, needle: string): boolean {
  if (position + needle.length > input.length) return false;
  for (let i = 0; i < needle.length; i++) {
    if (input[position + i] !== needle[i]) return false;
  }
  return true;
}

/** Remove a single trailing newline from a code-block body, if present. */
function stripTrailingNewline(body: string): string {
  if (body.endsWith('\n')) return body.substring(0, body.length - 1);
  return body;
}
